// src/services/financialExcelService.js
// Updated: Sunday, July 19, 2026 at 11:22 PM EDT

import * as XLSX from 'xlsx';
import {
  collection,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';

export const FINANCIAL_COLLECTIONS = [
  'funds',
  'transactions',
  'batches',
  'expenses',
  'expenseCategories',
  'vendors',
  'givingUnits',
  'donationTypes',
  'budgets',
  'assets',
  'liabilities',
  'hidden_bank_items',
  'auditLog',
  'reconciliationSessions',
  'importJobs',
  'exportJobs',
];

const SHEET_NAME_BY_COLLECTION = {
  funds: 'Funds',
  transactions: 'Transactions',
  batches: 'Batches',
  expenses: 'Expenses',
  expenseCategories: 'Expense_Categories',
  vendors: 'Vendors',
  givingUnits: 'Giving_Units',
  donationTypes: 'Donation_Types',
  budgets: 'Budgets',
  assets: 'Assets',
  liabilities: 'Liabilities',
  hidden_bank_items: 'Hidden_Bank_Items',
  auditLog: 'Audit_Log',
  reconciliationSessions: 'Reconciliation',
  importJobs: 'Import_Jobs',
  exportJobs: 'Export_Jobs',
};

const COLLECTION_BY_SHEET_NAME = Object.fromEntries(
  Object.entries(SHEET_NAME_BY_COLLECTION).map(([collectionName, sheetName]) => [sheetName, collectionName]),
);

const HISTORICAL_INCOME_HEADERS = [
  'External Record ID',
  'Contribution Date',
  'Donor ID',
  'Giving Unit ID',
  'Guest Donor Name',
  'Amount',
  'Fund ID',
  'Donation Type ID',
  'Payment Method',
  'Check Number',
  'Reference Number',
  'Tax Deductible',
  'Statement Eligible',
  'Memo',
  'Original Batch Number',
  'Status',
  'Reconciled',
  'Reconciled Date',
  'Source System',
];

const HISTORICAL_EXPENSE_HEADERS = [
  'External Record ID',
  'Expense Date',
  'Vendor ID',
  'Vendor Name',
  'Payee',
  'Amount',
  'Fund ID',
  'Expense Category ID',
  'Description',
  'Payment Method',
  'Check Number',
  'Reference Number',
  'Status',
  'Approved By UID',
  'Approved By Name',
  'Approved Date',
  'Reconciled',
  'Reconciled Date',
  'Source System',
  'Memo',
];

function encodeFirestoreValue(value) {
  if (value === null || value === undefined) return value ?? null;

  if (typeof value?.toDate === 'function') {
    return { __firestoreType: 'timestamp', value: value.toDate().toISOString() };
  }

  if (typeof value?.path === 'string' && value?.firestore) {
    return { __firestoreType: 'reference', value: value.path };
  }

  if (typeof value?.latitude === 'number' && typeof value?.longitude === 'number') {
    return {
      __firestoreType: 'geopoint',
      latitude: value.latitude,
      longitude: value.longitude,
    };
  }

  if (Array.isArray(value)) return value.map(encodeFirestoreValue);

  if (typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, nestedValue]) => [key, encodeFirestoreValue(nestedValue)]),
    );
  }

  return value;
}

function simplifyCellValue(value) {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value;
  if (value?.__firestoreType === 'timestamp') return value.value;
  if (value?.__firestoreType === 'reference') return value.value;
  return JSON.stringify(value);
}

function flattenTopLevelDocument(documentId, documentPath, encodedData) {
  const row = {
    documentId,
    documentPath,
  };

  Object.entries(encodedData).forEach(([key, value]) => {
    row[key] = simplifyCellValue(value);
  });

  row.rawJson = JSON.stringify(encodedData);
  return row;
}

function styleWorksheet(worksheet, rows) {
  const headers = rows.length > 0 ? Object.keys(rows[0]) : [];
  worksheet['!freeze'] = { xSplit: 0, ySplit: 1 };
  worksheet['!autofilter'] = headers.length > 0 ? { ref: `A1:${XLSX.utils.encode_col(headers.length - 1)}1` } : undefined;
  worksheet['!cols'] = headers.map((header) => {
    if (header === 'rawJson') return { wch: 55 };
    if (/description|memo|note|address/i.test(header)) return { wch: 30 };
    if (/date|At$|timestamp/i.test(header)) return { wch: 22 };
    if (/amount|balance|total/i.test(header)) return { wch: 14 };
    return { wch: Math.min(Math.max(header.length + 2, 12), 24) };
  });
}

function appendJsonSheet(workbook, sheetName, rows, emptyMessage = 'No records were found.') {
  const outputRows = rows.length > 0 ? rows : [{ message: emptyMessage }];
  const worksheet = XLSX.utils.json_to_sheet(outputRows);
  styleWorksheet(worksheet, outputRows);
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
}

function downloadWorkbook(workbook, fileName) {
  XLSX.writeFile(workbook, fileName, { compression: true });
}

function createManifestRows({ churchId, churchName, user, counts }) {
  return [
    { Field: 'Backup Format Version', Value: '1.0' },
    { Field: 'Export Type', Value: 'FULL_FINANCIAL_EXCEL_BACKUP' },
    { Field: 'Church ID', Value: churchId },
    { Field: 'Church Name', Value: churchName || '' },
    { Field: 'Exported At', Value: new Date().toISOString() },
    { Field: 'Exported By UID', Value: user?.uid || '' },
    { Field: 'Exported By Email', Value: user?.email || '' },
    { Field: 'Source Project', Value: 'my-church-app-2' },
    { Field: 'Record Counts', Value: JSON.stringify(counts) },
    { Field: 'Restore Notes', Value: 'Use the in-app Backup & Bulk Import screen. Validate before restoring.' },
  ];
}

export async function exportCompleteFinancialWorkbook({ churchId, churchName, user }) {
  if (!churchId) throw new Error('A church ID is required to export financial data.');

  const workbook = XLSX.utils.book_new();
  const counts = {};

  const collectionResults = await Promise.all(
    FINANCIAL_COLLECTIONS.map(async (collectionName) => {
      const snapshot = await getDocs(
        collection(db, 'churches', churchId, 'financials', 'data', collectionName),
      );

      const rows = snapshot.docs.map((documentSnapshot) => {
        const encodedData = encodeFirestoreValue(documentSnapshot.data());
        return flattenTopLevelDocument(
          documentSnapshot.id,
          documentSnapshot.ref.path,
          encodedData,
        );
      });

      counts[collectionName] = rows.length;
      return { collectionName, rows };
    }),
  );

  const userSnapshot = await getDocs(query(collection(db, 'users'), where('churchId', '==', churchId)));
  const financialUsers = userSnapshot.docs
    .filter((userDoc) => {
      const data = userDoc.data();
      return Boolean(data.financeRole || data.financialIdentifiers || data.isIndependentGiver);
    })
    .map((userDoc) => flattenTopLevelDocument(
      userDoc.id,
      userDoc.ref.path,
      encodeFirestoreValue(userDoc.data()),
    ));
  counts.financialUsers = financialUsers.length;

  const manifestRows = createManifestRows({ churchId, churchName, user, counts });
  const manifestSheet = XLSX.utils.json_to_sheet(manifestRows);
  manifestSheet['!cols'] = [{ wch: 28 }, { wch: 80 }];
  XLSX.utils.book_append_sheet(workbook, manifestSheet, 'Backup_Manifest');

  collectionResults.forEach(({ collectionName, rows }) => {
    appendJsonSheet(workbook, SHEET_NAME_BY_COLLECTION[collectionName], rows);
  });

  appendJsonSheet(workbook, 'Financial_Users', financialUsers);

  const splitRows = [];
  const transactions = collectionResults.find((result) => result.collectionName === 'transactions')?.rows || [];
  transactions.forEach((transactionRow) => {
    try {
      const raw = JSON.parse(transactionRow.rawJson);
      if (Array.isArray(raw.splits)) {
        raw.splits.forEach((split, index) => {
          splitRows.push({
            parentTransactionId: transactionRow.documentId,
            splitIndex: index,
            ...Object.fromEntries(
              Object.entries(split).map(([key, value]) => [key, simplifyCellValue(value)]),
            ),
            rawJson: JSON.stringify(split),
          });
        });
      }
    } catch (error) {
      console.warn('Unable to export transaction splits for', transactionRow.documentId, error);
    }
  });
  appendJsonSheet(workbook, 'Transaction_Splits', splitRows);

  const fileDate = new Date().toISOString().slice(0, 10);
  const safeChurchName = (churchName || churchId).replace(/[^a-z0-9-_]+/gi, '_');
  downloadWorkbook(workbook, `${safeChurchName}_Financial_Backup_${fileDate}.xlsx`);

  return counts;
}

export function downloadHistoricalIncomeTemplate() {
  const workbook = XLSX.utils.book_new();
  const instructions = [
    { Instruction: 'Enter one contribution per row. Do not change the worksheet name.' },
    { Instruction: 'Required fields: Contribution Date, Amount, and Fund ID.' },
    { Instruction: 'Use TRUE or FALSE for Tax Deductible, Statement Eligible, and Reconciled.' },
    { Instruction: 'Status may be draft, pending_approval, posted, posted_historical, rejected, or voided.' },
    { Instruction: 'External Record ID should be unique when available to prevent duplicate imports.' },
  ];
  const instructionSheet = XLSX.utils.json_to_sheet(instructions);
  instructionSheet['!cols'] = [{ wch: 110 }];
  XLSX.utils.book_append_sheet(workbook, instructionSheet, 'Instructions');

  const worksheet = XLSX.utils.aoa_to_sheet([HISTORICAL_INCOME_HEADERS]);
  worksheet['!cols'] = HISTORICAL_INCOME_HEADERS.map((header) => ({ wch: Math.min(Math.max(header.length + 2, 14), 28) }));
  worksheet['!freeze'] = { xSplit: 0, ySplit: 1 };
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Historical_Income');
  downloadWorkbook(workbook, 'Historical_Income_Import_Template.xlsx');
}

export function downloadHistoricalExpenseTemplate() {
  const workbook = XLSX.utils.book_new();
  const instructions = [
    { Instruction: 'Enter one expense per row. Do not change the worksheet name.' },
    { Instruction: 'Required fields: Expense Date, Amount, Fund ID, and Payee or Vendor Name.' },
    { Instruction: 'Use TRUE or FALSE for Reconciled.' },
    { Instruction: 'Status may be draft, pending_approval, approved, approved_historical, rejected, or reversed.' },
    { Instruction: 'External Record ID should be unique when available to prevent duplicate imports.' },
  ];
  const instructionSheet = XLSX.utils.json_to_sheet(instructions);
  instructionSheet['!cols'] = [{ wch: 110 }];
  XLSX.utils.book_append_sheet(workbook, instructionSheet, 'Instructions');

  const worksheet = XLSX.utils.aoa_to_sheet([HISTORICAL_EXPENSE_HEADERS]);
  worksheet['!cols'] = HISTORICAL_EXPENSE_HEADERS.map((header) => ({ wch: Math.min(Math.max(header.length + 2, 14), 28) }));
  worksheet['!freeze'] = { xSplit: 0, ySplit: 1 };
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Historical_Expenses');
  downloadWorkbook(workbook, 'Historical_Expense_Import_Template.xlsx');
}

function parseBoolean(value) {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  return ['true', 'yes', 'y', '1'].includes(String(value || '').trim().toLowerCase());
}

function parseNumber(value) {
  const parsed = Number(String(value ?? '').replace(/[$,]/g, '').trim());
  return Number.isFinite(parsed) ? parsed : null;
}

function cleanRow(row) {
  return Object.fromEntries(
    Object.entries(row).map(([key, value]) => [String(key).trim(), value]),
  );
}

function parseBackupManifest(workbook) {
  const sheet = workbook.Sheets.Backup_Manifest;
  if (!sheet) return null;
  const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
  return Object.fromEntries(rows.map((row) => [row.Field, row.Value]));
}

function parseRestoreRecords(workbook) {
  const records = [];
  const errors = [];

  Object.entries(COLLECTION_BY_SHEET_NAME).forEach(([sheetName, collectionName]) => {
    const sheet = workbook.Sheets[sheetName];
    if (!sheet) return;

    const rows = XLSX.utils.sheet_to_json(sheet, { defval: '', raw: false });
    rows.forEach((rawRow, index) => {
      const row = cleanRow(rawRow);
      if (!row.documentId || !row.rawJson) {
        if (row.message) return;
        errors.push(`${sheetName} row ${index + 2}: documentId and rawJson are required.`);
        return;
      }

      try {
        records.push({
          collectionName,
          documentId: String(row.documentId),
          data: JSON.parse(row.rawJson),
        });
      } catch (error) {
        errors.push(`${sheetName} row ${index + 2}: rawJson is not valid JSON.`);
      }
    });
  });

  const usersSheet = workbook.Sheets.Financial_Users;
  if (usersSheet) {
    const rows = XLSX.utils.sheet_to_json(usersSheet, { defval: '', raw: false });
    rows.forEach((rawRow, index) => {
      const row = cleanRow(rawRow);
      if (!row.documentId || !row.rawJson) {
        if (!row.message) errors.push(`Financial_Users row ${index + 2}: documentId and rawJson are required.`);
        return;
      }
      try {
        records.push({
          collectionName: '__financialUsers',
          documentId: String(row.documentId),
          data: JSON.parse(row.rawJson),
        });
      } catch (error) {
        errors.push(`Financial_Users row ${index + 2}: rawJson is not valid JSON.`);
      }
    });
  }

  return { records, errors };
}

function parseHistoricalIncome(workbook) {
  const sheet = workbook.Sheets.Historical_Income;
  if (!sheet) return { records: [], errors: ['The Historical_Income worksheet is missing.'] };

  const rows = XLSX.utils.sheet_to_json(sheet, { defval: '', raw: false });
  const records = [];
  const errors = [];

  rows.forEach((rawRow, index) => {
    const row = cleanRow(rawRow);
    if (Object.values(row).every((value) => String(value).trim() === '')) return;

    const amount = parseNumber(row['Amount']);
    const date = String(row['Contribution Date'] || '').trim();
    const fundId = String(row['Fund ID'] || '').trim();

    if (!date) errors.push(`Historical_Income row ${index + 2}: Contribution Date is required.`);
    if (amount === null || amount <= 0) errors.push(`Historical_Income row ${index + 2}: Amount must be greater than zero.`);
    if (!fundId) errors.push(`Historical_Income row ${index + 2}: Fund ID is required.`);

    records.push({
      collectionName: 'transactions',
      documentId: String(row['External Record ID'] || '').trim() || null,
      data: {
        externalRecordId: String(row['External Record ID'] || '').trim() || null,
        date,
        amount,
        donorId: String(row['Donor ID'] || '').trim() || null,
        givingUnitId: String(row['Giving Unit ID'] || '').trim() || null,
        guestDonorName: String(row['Guest Donor Name'] || '').trim() || null,
        fundId,
        donationTypeId: String(row['Donation Type ID'] || '').trim() || null,
        paymentMethod: String(row['Payment Method'] || '').trim() || 'Other',
        checkNumber: String(row['Check Number'] || '').trim() || null,
        referenceNumber: String(row['Reference Number'] || '').trim() || null,
        taxDeductible: parseBoolean(row['Tax Deductible']),
        statementEligible: parseBoolean(row['Statement Eligible']),
        note: String(row['Memo'] || '').trim() || null,
        originalBatchNumber: String(row['Original Batch Number'] || '').trim() || null,
        status: String(row['Status'] || '').trim() || 'posted_historical',
        reconciled: parseBoolean(row['Reconciled']),
        reconciledDate: String(row['Reconciled Date'] || '').trim() || null,
        sourceSystem: String(row['Source System'] || '').trim() || 'Excel historical import',
        type: 'income',
        historicalImport: true,
      },
    });
  });

  return { records, errors };
}

function parseHistoricalExpenses(workbook) {
  const sheet = workbook.Sheets.Historical_Expenses;
  if (!sheet) return { records: [], errors: ['The Historical_Expenses worksheet is missing.'] };

  const rows = XLSX.utils.sheet_to_json(sheet, { defval: '', raw: false });
  const records = [];
  const errors = [];

  rows.forEach((rawRow, index) => {
    const row = cleanRow(rawRow);
    if (Object.values(row).every((value) => String(value).trim() === '')) return;

    const amount = parseNumber(row['Amount']);
    const date = String(row['Expense Date'] || '').trim();
    const fundId = String(row['Fund ID'] || '').trim();
    const payee = String(row['Payee'] || row['Vendor Name'] || '').trim();

    if (!date) errors.push(`Historical_Expenses row ${index + 2}: Expense Date is required.`);
    if (amount === null || amount <= 0) errors.push(`Historical_Expenses row ${index + 2}: Amount must be greater than zero.`);
    if (!fundId) errors.push(`Historical_Expenses row ${index + 2}: Fund ID is required.`);
    if (!payee) errors.push(`Historical_Expenses row ${index + 2}: Payee or Vendor Name is required.`);

    records.push({
      collectionName: 'expenses',
      documentId: String(row['External Record ID'] || '').trim() || null,
      data: {
        externalRecordId: String(row['External Record ID'] || '').trim() || null,
        date,
        vendorId: String(row['Vendor ID'] || '').trim() || null,
        vendorName: String(row['Vendor Name'] || '').trim() || null,
        payee,
        amount,
        fundId,
        categoryId: String(row['Expense Category ID'] || '').trim() || null,
        description: String(row['Description'] || '').trim() || null,
        paymentMethod: String(row['Payment Method'] || '').trim() || 'Other',
        checkNumber: String(row['Check Number'] || '').trim() || null,
        referenceNumber: String(row['Reference Number'] || '').trim() || null,
        status: String(row['Status'] || '').trim() || 'approved_historical',
        approvedBy: String(row['Approved By UID'] || '').trim() || null,
        approvedByName: String(row['Approved By Name'] || '').trim() || null,
        approvedDate: String(row['Approved Date'] || '').trim() || null,
        reconciled: parseBoolean(row['Reconciled']),
        reconciledDate: String(row['Reconciled Date'] || '').trim() || null,
        sourceSystem: String(row['Source System'] || '').trim() || 'Excel historical import',
        note: String(row['Memo'] || '').trim() || null,
        historicalImport: true,
      },
    });
  });

  return { records, errors };
}

export async function readAndValidateFinancialWorkbook(file, importMode, expectedChurchId) {
  const arrayBuffer = await file.arrayBuffer();
  const workbook = XLSX.read(arrayBuffer, { type: 'array', cellDates: false });

  let result;
  let manifest = null;

  if (importMode === 'restore_exact' || importMode === 'merge_missing' || importMode === 'replace_matching') {
    manifest = parseBackupManifest(workbook);
    if (!manifest) {
      return {
        records: [],
        errors: ['Backup_Manifest is required for a backup restore.'],
        warnings: [],
        manifest: null,
      };
    }

    if (String(manifest['Church ID']) !== String(expectedChurchId)) {
      return {
        records: [],
        errors: [`This backup belongs to church ${manifest['Church ID']}, not ${expectedChurchId}.`],
        warnings: [],
        manifest,
      };
    }

    result = parseRestoreRecords(workbook);
  } else if (importMode === 'historical_income') {
    result = parseHistoricalIncome(workbook);
  } else if (importMode === 'historical_expenses') {
    result = parseHistoricalExpenses(workbook);
  } else {
    result = { records: [], errors: ['Unsupported import mode.'] };
  }

  const warnings = [];
  const duplicateKeys = new Set();
  const seenKeys = new Set();
  result.records.forEach((record, index) => {
    const key = `${record.collectionName}:${record.documentId || record.data?.externalRecordId || index}`;
    if (seenKeys.has(key)) duplicateKeys.add(key);
    seenKeys.add(key);
  });
  if (duplicateKeys.size > 0) {
    warnings.push(`${duplicateKeys.size} duplicate record identifiers were detected inside the workbook.`);
  }

  return {
    ...result,
    warnings,
    manifest,
    sheetNames: workbook.SheetNames,
  };
}

export async function importFinancialRecords({ churchId, importMode, records, fileName }) {
  const callable = httpsCallable(functions, 'importFinancialExcelRecords');
  const importSessionId = crypto.randomUUID();
  const chunkSize = 125;
  const summary = {
    total: records.length,
    created: 0,
    updated: 0,
    skipped: 0,
    failed: 0,
    errors: [],
  };

  for (let start = 0; start < records.length; start += chunkSize) {
    const chunk = records.slice(start, start + chunkSize);
    const result = await callable({
      churchId,
      importMode,
      records: chunk,
      importSessionId,
      fileName,
      isFinalChunk: start + chunkSize >= records.length,
    });

    const data = result.data || {};
    summary.created += data.created || 0;
    summary.updated += data.updated || 0;
    summary.skipped += data.skipped || 0;
    summary.failed += data.failed || 0;
    if (Array.isArray(data.errors)) summary.errors.push(...data.errors);
  }

  return summary;
}
